import { Controller } from '@hotwired/stimulus'
import { motion, reducedMotion } from '../libs/motion'

// Open dialogs, so the page behind stays still until the last one closes.
let openDialogs = 0

export default class extends Controller {
  // `scroller` marks the part that scrolls when it isn't the body itself (a list in one pane).
  static targets = ['content', 'header', 'body', 'footer', 'scroller']

  connect() {
    // Focus management (WCAG 2.4.3): remember what was focused, move focus into
    // the dialog, and trap Tab within it while open. Only real dialogs get this
    // treatment — some pages attach this controller to a plain page wrapper,
    // where trapping focus would wrongly hijack keyboard navigation.
    if (this.#isDialog) {
      this.#previouslyFocused = document.activeElement
      this.#trapHandler = this.#trapFocus.bind(this)
      this.element.addEventListener('keydown', this.#trapHandler)
      this.#focusInitial()
      this.#lockScroll()
      this.#enter()
    }

    if (this.hasBodyTarget) {
      this.#handleScroll = this.#updateScrollBorders.bind(this)
      this.#scroller.addEventListener('scroll', this.#handleScroll)

      // The scroller's own size, and each part inside it, which change as panels show and hide.
      this.#resizeObserver = new ResizeObserver(this.#handleScroll)
      ;[this.#scroller, ...this.#scroller.children].forEach(el => this.#resizeObserver.observe(el))

      this.#updateScrollBorders()
    }
  }

  disconnect() {
    if (this.hasBodyTarget) {
      this.#scroller.removeEventListener('scroll', this.#handleScroll)
    }
    this.#resizeObserver?.disconnect()
    if (this.#locked) this.#unlockScroll()

    if (this.#trapHandler) {
      this.element.removeEventListener('keydown', this.#trapHandler)
      // Restore focus to the element that opened the modal.
      if (this.#previouslyFocused?.isConnected) {
        this.#previouslyFocused.focus()
      }
    }
  }

  async close() {
    if (this.#closing) return
    this.#closing = true
    await this.#leave()
    this.element.parentElement?.removeAttribute('src')
    this.element.remove()
  }

  // For links that load a frame elsewhere on the page: closing on click would
  // detach the link before Turbo follows it, so wait for that frame instead.
  // A `frame` param names it, so other frames loading meanwhile don't count.
  closeOnFrameLoad({ target, params: { frame } }) {
    if (frame && target.id !== frame) return
    if (!target.contains(this.element)) this.close()
  }

  backdropClose(event) {
    if (!event.target.isConnected) return
    if (!this.contentTarget.contains(event.target)) {
      this.close()
    }
  }

  // Private

  #handleScroll
  #resizeObserver
  #previouslyFocused
  #trapHandler
  #locked = false
  #closing = false

  get #scroller() {
    return this.hasScrollerTarget ? this.scrollerTarget : this.bodyTarget
  }

  // Holds the page still, keeping its scrollbar's width so nothing shifts sideways.
  #lockScroll() {
    this.#locked = true
    if (openDialogs++ > 0) return

    const root = document.documentElement
    const scrollbar = window.innerWidth - root.clientWidth
    root.style.overflow = 'hidden'
    if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`
  }

  #unlockScroll() {
    this.#locked = false
    if (--openDialogs > 0) return

    const root = document.documentElement
    root.style.overflow = ''
    root.style.paddingRight = ''
  }

  // The backdrop fades in and the dialog rises into place, there from the first frame
  // (a fade would make it feel slower to arrive); leaving, both fade out.
  #enter() {
    if (reducedMotion() || !this.hasContentTarget) return

    this.#backdrop?.animate([{ opacity: 0 }, { opacity: 1 }], motion('fade'))
    this.contentTarget.animate([{ transform: 'translateY(16px)' }, { transform: 'none' }], motion('spring'))
  }

  async #leave() {
    if (reducedMotion() || !this.hasContentTarget) return

    const leaving = { ...motion('fade'), fill: 'forwards' }
    await Promise.all([
      this.#backdrop?.animate([{ opacity: 1 }, { opacity: 0 }], leaving).finished,
      this.contentTarget.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(8px)' }], leaving).finished
    ])
  }

  get #backdrop() {
    return this.element.querySelector(':scope > .bg-overlay')
  }

  get #isDialog() {
    return this.element.getAttribute('role') === 'dialog'
  }

  #focusContainer() {
    return this.hasContentTarget ? this.contentTarget : this.element
  }

  #focusableElements() {
    const selector = [
      'a[href]:not([tabindex="-1"])', 'button:not([disabled])', 'textarea:not([disabled])',
      'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',')
    return Array.from(this.#focusContainer().querySelectorAll(selector))
      .filter((el) => el.offsetParent !== null)
  }

  #focusInitial() {
    const focusable = this.#focusableElements()
    const autofocused = focusable.find((el) => el.hasAttribute('autofocus'))
    if (focusable.length) {
      (autofocused || focusable[0]).focus()
    } else {
      const container = this.#focusContainer()
      container.setAttribute('tabindex', '-1')
      container.focus()
    }
  }

  #trapFocus(event) {
    if (event.key !== 'Tab') return

    const focusable = this.#focusableElements()
    if (!focusable.length) {
      event.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  #updateScrollBorders() {
    const body = this.#scroller
    const scrolledDown = body.scrollTop > 0
    const scrolledToBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 1

    if (this.hasHeaderTarget) {
      this.headerTarget.classList.toggle('border-contrast-low', scrolledDown)
      this.headerTarget.classList.toggle('border-transparent', !scrolledDown)
    }

    if (this.hasFooterTarget) {
      const hasOverflow = body.scrollHeight > body.clientHeight
      const showBorder = hasOverflow && !scrolledToBottom

      this.footerTarget.classList.toggle('border-contrast-low', showBorder)
      this.footerTarget.classList.toggle('border-transparent', !showBorder)
    }
  }
}
